import { useCallback } from "react";
import { type MutationHookOptions } from "@apollo/client";
import { useUpdateVaultDocumentMutation } from "#graphql/supergraphHooks";
import {
  type UpdateVaultDocumentInput,
  type UpdateVaultDocumentMutation,
  type UpdateVaultDocumentMutationVariables,
} from "#graphql/supergraphTypes";

export interface UseUpdateVaultDocumentReturn {
  updateVaultDocument: (
    input: UpdateVaultDocumentInput,
  ) => Promise<UpdateVaultDocumentMutation["updateVaultDocument"] | null>;
  loading: boolean;
  error: ReturnType<typeof useUpdateVaultDocumentMutation>[1]["error"];
}

/**
 * DV-422 — edit a filed document's attribute values. Id-shaped attributes
 * (reference / select) carry their picked ids in `valueIds`, scalars in the
 * matching `valueText` / `valueNumber` / `valueDate` / `valueBoolean` column
 * (DV-466). Returns the updated document node (same fields Documents search
 * selects), or `null` if the mutation returned no data. The caller maps it with
 * `apiVaultDocumentToVaultDocument(node, lookups)`.
 *
 * `input.attributeValues` UPSERTS per attribute — it does NOT replace the
 * document's set, so an omitted attribute keeps its current value and a partial
 * edit is valid. An omission therefore cannot clear a value: see the mutation's
 * comment in `mutations.federation.gql` for what clearing does and doesn't allow.
 */
export const useUpdateVaultDocument = (
  options?: MutationHookOptions<
    UpdateVaultDocumentMutation,
    UpdateVaultDocumentMutationVariables
  >,
): UseUpdateVaultDocumentReturn => {
  const [mutate, { loading, error }] = useUpdateVaultDocumentMutation(options);

  const updateVaultDocument = useCallback(
    async (input: UpdateVaultDocumentInput) => {
      const { data } = await mutate({ variables: { input } });
      return data?.updateVaultDocument ?? null;
    },
    [mutate],
  );

  return { updateVaultDocument, loading, error };
};
